import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import OnboardingTour from "@/components/OnboardingTour";
import HelpChatbot from "@/components/HelpChatbot";
import { LayoutDashboard, Radar, FileText, KanbanSquare, BookOpen, Building2, LogOut, Upload, Zap, CreditCard, Clock, BarChart3, Bell, Users, FileUp, Activity, Palette, TrendingUp, Handshake, Sparkles, Database, Award, Wand2, FolderOpen, Layers, Eye, Ruler, Bot } from "lucide-react";
import Logo from "@/components/Logo";
import UsageMeter from "@/components/UsageMeter";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// Each nav item gets a distinct colorful family so the sidebar reads like a
// squad of agents rather than a monotone list.
const nav = [
  { to: "/dashboard",       label: "Command",        icon: LayoutDashboard, tid: "nav-dashboard",       tone: "violet" },
  { to: "/opportunities",   label: "Opportunities",  icon: Radar,           tid: "nav-opportunities",   tone: "indigo" },
  { to: "/browsed",         label: "Recently Viewed",icon: Eye,             tid: "nav-browsed",         tone: "sky" },
  { to: "/forecaster",      label: "Forecaster",     icon: TrendingUp,      tid: "nav-forecaster",      tone: "sky" },
  { to: "/connect",         label: "Connect",        icon: Handshake,       tid: "nav-connect",         tone: "emerald" },
  { to: "/bid-due",         label: "Bid Due",        icon: Clock,           tid: "nav-bid-due",         tone: "rose" },
  { to: "/rfp",             label: "RFP Analyzer",   icon: FileUp,          tid: "nav-rfp",             tone: "orange" },
  { to: "/takeoffs",        label: "Takeoffs",       icon: Ruler,           tid: "nav-takeoffs",        tone: "amber" },
  { to: "/pipeline",        label: "Pipeline",       icon: KanbanSquare,    tid: "nav-pipeline",        tone: "emerald" },
  { to: "/analytics",       label: "Analytics",      icon: BarChart3,       tid: "nav-analytics",       tone: "sky" },
  { to: "/proposals",       label: "Proposals",      icon: FileText,        tid: "nav-proposals",       tone: "fuchsia" },
  { to: "/autonomous",      label: "Auto-Agent",     icon: Zap,             tid: "nav-autonomous",      tone: "amber" },
  { to: "/saved-searches",  label: "Search Alerts",  icon: Bell,            tid: "nav-alerts",          tone: "teal" },
  { to: "/knowledge",       label: "Knowledge",      icon: BookOpen,        tid: "nav-knowledge",       tone: "yellow" },
  { to: "/documents",       label: "Documents",      icon: Upload,          tid: "nav-documents",       tone: "sky" },
  { to: "/pricing-history", label: "Price History",  icon: Database,        tid: "nav-pricing-history", tone: "yellow" },
  { to: "/wrap-rates",      label: "Wrap Rates",     icon: Layers,          tid: "nav-wrap-rates",      tone: "violet" },
  { to: "/past-performance",label: "Past Performance",icon: Award,           tid: "nav-past-performance",tone: "orange" },
  { to: "/submittals",      label: "Submittals",     icon: FolderOpen,      tid: "nav-submittals",      tone: "cyan" },
  { to: "/bid-skills",      label: "House Style",     icon: Wand2,           tid: "nav-bid-skills",      tone: "fuchsia" },
  { to: "/activity",        label: "Activity",       icon: Activity,        tid: "nav-activity",        tone: "pink" },
  { to: "/profile",         label: "Company",        icon: Building2,       tid: "nav-profile",         tone: "indigo" },
  { to: "/branding",        label: "Branding",       icon: Palette,         tid: "nav-branding",        tone: "fuchsia" },
  { to: "/team",            label: "Team",           icon: Users,           tid: "nav-team",            tone: "emerald" },
  { to: "/integrations",    label: "AI Assistant",   icon: Bot,             tid: "nav-integrations",    tone: "fuchsia" },
  { to: "/billing",         label: "Billing",        icon: CreditCard,      tid: "nav-billing",         tone: "violet" },
];

const toneMap = {
  violet:  { tile: "bg-violet-100 text-violet-600",   bar: "bg-violet-500",   hoverTile: "group-hover:bg-violet-100" },
  indigo:  { tile: "bg-indigo-100 text-indigo-600",   bar: "bg-indigo-500",   hoverTile: "group-hover:bg-indigo-100" },
  rose:    { tile: "bg-rose-100 text-rose-600",       bar: "bg-rose-500",     hoverTile: "group-hover:bg-rose-100" },
  orange:  { tile: "bg-orange-100 text-orange-600",   bar: "bg-orange-500",   hoverTile: "group-hover:bg-orange-100" },
  emerald: { tile: "bg-emerald-100 text-emerald-600", bar: "bg-emerald-500",  hoverTile: "group-hover:bg-emerald-100" },
  sky:     { tile: "bg-sky-100 text-sky-600",         bar: "bg-sky-500",      hoverTile: "group-hover:bg-sky-100" },
  fuchsia: { tile: "bg-fuchsia-100 text-fuchsia-600", bar: "bg-fuchsia-500",  hoverTile: "group-hover:bg-fuchsia-100" },
  amber:   { tile: "bg-amber-100 text-amber-600",     bar: "bg-amber-500",    hoverTile: "group-hover:bg-amber-100" },
  teal:    { tile: "bg-teal-100 text-teal-600",       bar: "bg-teal-500",     hoverTile: "group-hover:bg-teal-100" },
  yellow:  { tile: "bg-yellow-100 text-yellow-700",   bar: "bg-yellow-500",   hoverTile: "group-hover:bg-yellow-100" },
  pink:    { tile: "bg-pink-100 text-pink-600",       bar: "bg-pink-500",     hoverTile: "group-hover:bg-pink-100" },
  cyan:    { tile: "bg-cyan-100 text-cyan-600",       bar: "bg-cyan-500",     hoverTile: "group-hover:bg-cyan-100" },
};

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    let ok = true;
    const load = async () => {
      try { const r = await api.get("/credits/status"); if (ok) setCredits(r.data); }
      catch {}
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { ok = false; clearInterval(iv); };
  }, [loc.pathname]);

  const remaining = credits?.remaining_total ?? null;
  const low = remaining !== null && remaining < 20;
  const outta = remaining !== null && remaining <= 0;

  return (
    <div className="bf-light min-h-screen text-slate-900 flex relative" style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #FAF5FF 60%, #FFF1F2 100%)" }}>
      {/* Ambient colorful blobs */}
      <div aria-hidden className="pointer-events-none fixed top-0 -right-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-40" style={{ background: "radial-gradient(circle, rgba(236,72,153,0.20), transparent 70%)" }} />
      <div aria-hidden className="pointer-events-none fixed -bottom-20 -left-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-40" style={{ background: "radial-gradient(circle, rgba(99,102,241,0.20), transparent 70%)" }} />

      <aside className="w-64 shrink-0 bg-white/85 backdrop-blur-xl border-r border-slate-200/70 flex flex-col z-10 relative" data-testid="app-sidebar">
        <div className="p-6 border-b border-slate-200/70">
          <Link to="/dashboard" className="flex items-center gap-2 group" data-testid="sidebar-logo">
            <Logo className="w-9 h-9" />
            <div>
              <div className="font-display font-black text-lg leading-none tracking-tight text-slate-900">BIDFORGE</div>
              <div className="mono-label mt-1" style={{ color: "#8B5CF6" }}>AI · GOV · BD</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {nav.map((n) => {
            const active = loc.pathname === n.to || loc.pathname.startsWith(n.to + "/");
            const Icon = n.icon;
            const t = toneMap[n.tone] || toneMap.violet;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.tid}
                className={`group relative flex items-center gap-3 px-2.5 py-2 rounded-xl text-sm transition-all duration-200 ${
                  active
                    ? "bg-slate-50 text-slate-900 shadow-sm"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                {active && <span className={`absolute left-0 top-2 bottom-2 w-1 rounded-r-full ${t.bar}`} />}
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-transform group-hover:scale-105 ${active ? t.tile : "bg-slate-100 text-slate-500"} ${!active ? t.hoverTile : ""} ${!active ? "group-hover:text-slate-700" : ""}`}>
                  <Icon className="w-4 h-4" strokeWidth={2} />
                </span>
                <span className="font-medium tracking-tight">{n.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-200/70">
          {/* AI credit chip — visible on every page */}
          <Link
            to="/credits"
            className={`mb-3 flex items-center gap-2 rounded-xl px-3 py-2 transition-all border ${
              outta ? "bg-rose-50 border-rose-200 hover:bg-rose-100" :
              low   ? "bg-amber-50 border-amber-200 hover:bg-amber-100" :
                      "bg-gradient-to-br from-violet-50 to-fuchsia-50 border-violet-200 hover:from-violet-100 hover:to-fuchsia-100"
            }`}
            data-testid="credits-chip"
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${outta ? "bg-rose-500 text-white" : low ? "bg-amber-500 text-white" : "bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white"}`}>
              <Sparkles className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500">AI CREDITS</div>
              <div className={`text-sm font-display font-bold ${outta ? "text-rose-700" : low ? "text-amber-700" : "text-slate-900"}`} data-testid="credits-chip-value">
                {remaining === null ? "…" : remaining.toLocaleString()}
              </div>
            </div>
            {(low || outta) && <span className="text-[10px] font-mono font-bold tracking-widest text-violet-600">TOP UP</span>}
          </Link>
          <UsageMeter />
          {user?.super_admin && (
            <Link to="/admin/usage" className="mb-3 flex items-center gap-2 rounded-xl px-3 py-2 bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-all" data-testid="admin-usage-link">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-900 text-white">
                <BarChart3 className="w-4 h-4" />
              </div>
              <div className="text-sm font-display font-bold text-slate-900">Admin usage</div>
            </Link>
          )}
          {user?.super_admin && (
            <Link to="/admin/routes-map" className="mb-3 flex items-center gap-2 rounded-xl px-3 py-2 bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-all" data-testid="admin-routes-map-link">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-indigo-600 text-white">
                <Layers className="w-4 h-4" />
              </div>
              <div className="text-sm font-display font-bold text-slate-900">Routes map</div>
            </Link>
          )}
          <div className="text-[10px] font-mono font-bold tracking-widest text-slate-400 mb-2">OPERATOR</div>
          <div className="text-sm font-semibold text-slate-900 truncate" data-testid="user-name">{user?.name || "—"}</div>
          <div className="text-xs text-slate-500 truncate mb-3">{user?.email}</div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono font-bold tracking-widest px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200">{(user?.plan || "starter").toUpperCase()}</span>
              {user?.role && user?.role !== "owner" && (
                <span className="text-[10px] font-mono font-bold tracking-widest px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200" data-testid="role-badge">{user.role.toUpperCase()}</span>
              )}
              {user?.role === "owner" && (
                <span className="text-[10px] font-mono font-bold tracking-widest px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid="role-badge">ADMIN</span>
              )}
            </div>
            <button
              onClick={async () => { await logout(); navigate("/"); }}
              className="text-slate-400 hover:text-rose-500 transition-colors"
              data-testid="logout-button"
              aria-label="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden relative z-0">{children}</main>
      <OnboardingTour user={user} />
      <HelpChatbot />
    </div>
  );
}
